<script setup>
import { ref, computed, toRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useWorkspaceV2 } from 'dashboard/composables/useWorkspaceV2';

import Button from 'dashboard/components-next/button/Button.vue';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';

const props = defineProps({
  activeSort: {
    type: String,
    default: 'name',
  },
  activeOrdering: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update:sort']);

const { t } = useI18n();
const { isWorkspaceV2 } = useWorkspaceV2();

const isMenuOpen = ref(false);
const sortButton = ref(null);
const closeMenu = () => {
  isMenuOpen.value = false;
  sortButton.value?.$el?.focus();
};

const sortMenus = [
  {
    label: t('COMPANIES.SORT_BY.OPTIONS.NAME'),
    value: 'name',
  },
  {
    label: t('COMPANIES.SORT_BY.OPTIONS.DOMAIN'),
    value: 'domain',
  },
  {
    label: t('COMPANIES.SORT_BY.OPTIONS.CREATED_AT'),
    value: 'created_at',
  },
  {
    label: t('COMPANIES.SORT_BY.OPTIONS.LAST_ACTIVITY_AT'),
    value: 'last_activity_at',
  },
  {
    label: t('COMPANIES.SORT_BY.OPTIONS.CONTACTS_COUNT'),
    value: 'contacts_count',
  },
];

const orderingMenus = [
  {
    label: t('COMPANIES.ORDER.OPTIONS.ASCENDING'),
    value: '',
  },
  {
    label: t('COMPANIES.ORDER.OPTIONS.DESCENDING'),
    value: '-',
  },
];

// Converted the props to refs for better reactivity
const activeSort = toRef(props, 'activeSort');

const activeOrdering = toRef(props, 'activeOrdering');

const activeSortLabel = computed(() => {
  const selectedMenu = sortMenus.find(menu => menu.value === activeSort.value);
  return selectedMenu?.label || t('COMPANIES.SORT_BY.LABEL');
});

const activeOrderingLabel = computed(() => {
  const selectedMenu = orderingMenus.find(
    menu => menu.value === activeOrdering.value
  );
  return selectedMenu?.label || t('COMPANIES.ORDER.LABEL');
});

const handleSortChange = value => {
  emit('update:sort', { sort: value, order: props.activeOrdering });
};

const handleOrderChange = value => {
  emit('update:sort', { sort: props.activeSort, order: value });
};
</script>

<template>
  <div class="relative">
    <Button
      ref="sortButton"
      icon="i-ri-swap-line"
      color="slate"
      size="sm"
      variant="ghost"
      :aria-label="t('COMPANIES.SORT_BY.LABEL')"
      :label="isWorkspaceV2 ? t('COMPANIES.SORT_BY.LABEL') : undefined"
      :aria-expanded="isMenuOpen"
      :class="[
        isMenuOpen ? 'bg-n-alpha-2' : '',
        isWorkspaceV2 ? 'min-h-11 !rounded-xl' : '',
      ]"
      @click="isMenuOpen = !isMenuOpen"
    />
    <div
      v-if="isMenuOpen"
      v-on-clickaway="() => (isMenuOpen = false)"
      :class="
        isWorkspaceV2
          ? '!left-0 !right-auto z-20 !rounded-xl !bg-n-solid-2'
          : ''
      "
      class="absolute top-full mt-1 ltr:-right-32 rtl:-left-32 sm:ltr:right-0 sm:rtl:left-0 flex flex-col gap-4 bg-n-alpha-3 backdrop-blur-[100px] border border-n-weak w-72 rounded-2xl p-4"
      @keydown.esc.stop.prevent="closeMenu"
    >
      <div v-if="isWorkspaceV2" class="space-y-3">
        <label class="block text-sm text-n-slate-12">
          {{ t('COMPANIES.SORT_BY.LABEL') }}
          <select
            :value="activeSort"
            class="mt-1 min-h-11 w-full rounded-xl border border-n-weak bg-n-solid-1 px-3"
            @change="handleSortChange($event.target.value)"
          >
            <option
              v-for="item in sortMenus"
              :key="item.value"
              :value="item.value"
            >
              {{ item.label }}
            </option>
          </select>
        </label>
        <label class="block text-sm text-n-slate-12">
          {{ t('COMPANIES.ORDER.LABEL') }}
          <select
            :value="activeOrdering"
            class="mt-1 min-h-11 w-full rounded-xl border border-n-weak bg-n-solid-1 px-3"
            @change="handleOrderChange($event.target.value)"
          >
            <option
              v-for="item in orderingMenus"
              :key="item.value"
              :value="item.value"
            >
              {{ item.label }}
            </option>
          </select>
        </label>
      </div>
      <template v-else>
        <div class="flex items-center justify-between gap-2">
          <span class="text-sm text-n-slate-12">
            {{ t('COMPANIES.SORT_BY.LABEL') }}
          </span>
          <SelectMenu
            :model-value="activeSort"
            :options="sortMenus"
            :label="activeSortLabel"
            sub-menu-position="left"
            @update:model-value="handleSortChange"
          />
        </div>
        <div class="flex items-center justify-between gap-2">
          <span class="text-sm text-n-slate-12">
            {{ t('COMPANIES.ORDER.LABEL') }}
          </span>
          <SelectMenu
            :model-value="activeOrdering"
            :options="orderingMenus"
            :label="activeOrderingLabel"
            sub-menu-position="left"
            @update:model-value="handleOrderChange"
          />
        </div>
      </template>
    </div>
  </div>
</template>
